
import React, { useMemo, useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { motion, AnimatePresence } from 'motion/react';
import { ArchiveFile } from '../types';

export interface TimelineEvent {
    id: string;
    name: string;
    startYear: number;
    endYear: number;
    category: string;
    articleId?: string;
    row?: number;
    color?: string;
}

interface TimelineChartProps {
  items: TimelineEvent[];
  articles: ArchiveFile[];
  isEditorMode?: boolean;
  onNavigate: (articleId: string) => void;
  onUpdate?: (items: TimelineEvent[]) => void;
  rowTitles?: string[];
  onUpdateRowTitles?: (titles: string[]) => void;
}

const CATEGORIES = [
    { label: 'Ancient Civilisations', color: '#f59e0b' },
    { label: 'History', color: '#10b981' },
    { label: 'Spirituality & Meditation', color: '#8b5cf6' },
    { label: 'Records', color: '#1e293b' }
];

const TimelineChart: React.FC<TimelineChartProps> = ({ 
    items, 
    articles,
    isEditorMode, 
    onNavigate,
    onUpdate,
    rowTitles = [],
    onUpdateRowTitles
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredItem, setHoveredItem] = useState<TimelineEvent | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<TimelineEvent | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [zoomRange, setZoomRange] = useState<[number, number] | null>(null);
  const [rowCount, setRowCount] = useState(1);
  const [editingRowIdx, setEditingRowIdx] = useState<number | null>(null);
  const [editingRowValue, setEditingRowValue] = useState<string>('');

  const handleSaveRowTitle = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingRowIdx === null || !onUpdateRowTitles) return;

    const newRowTitles = [...rowTitles];
    for (let j = 0; j <= editingRowIdx; j++) {
        if (newRowTitles[j] === undefined) {
            newRowTitles[j] = `Civilization ${j + 1}`;
        }
    }
    newRowTitles[editingRowIdx] = editingRowValue.trim() || `Civilization ${editingRowIdx + 1}`;
    onUpdateRowTitles(newRowTitles);
    setEditingRowIdx(null);
  };

  const timelineData = useMemo(() => {
    if (items.length === 0) {
        return [
            { id: '1', name: 'Mu & Lemuria', startYear: -50000, endYear: -10000, category: 'Ancient Civilisations', row: 0 },
            { id: '2', name: 'Atlantis: Rise', startYear: -28000, endYear: -18000, category: 'Ancient Civilisations', row: 1 },
            { id: '3', name: 'Atlantis: Peak', startYear: -18000, endYear: -10000, category: 'Ancient Civilisations', row: 1 },
            { id: '4', name: 'Final Destruction', startYear: -10500, endYear: -9500, category: 'Ancient Civilisations', row: 0 },
            { id: '5', name: 'Ancient Egypt: Early', startYear: -10500, endYear: -5000, category: 'History', row: 2 },
            { id: '6', name: 'Essene Community', startYear: -200, endYear: 100, category: 'Spirituality & Meditation', row: 3 },
            { id: '7', name: 'Edgar Cayce Life', startYear: 1877, endYear: 1945, category: 'Records', row: 4 },
        ] as TimelineEvent[];
    }
    return items;
  }, [items]);

  const fullExtent = useMemo(() => {
    const years = timelineData.flatMap(d => [d.startYear, d.endYear]);
    if (years.length === 0) return [-50000, 2025] as [number, number];
    const extent = d3.extent(years) as [number, number];
    const padding = (extent[1] - extent[0]) * 0.1;
    return [extent[0] - padding, extent[1] + padding] as [number, number];
  }, [timelineData]);

  useEffect(() => {
    if (!zoomRange && fullExtent) {
        setZoomRange(fullExtent);
    }
  }, [fullExtent, zoomRange]);

  useEffect(() => {
    if (timelineData.length > 0) {
        const maxRow = Math.max(...timelineData.map(d => d.row || 0));
        setRowCount(prev => Math.max(prev, maxRow + 1));
    }
  }, [timelineData]);

  const lanes = useMemo(() => {
    // If we have manual row assignments, use them.
    // Otherwise, fallback to auto-laning or default to row 0.
    return timelineData.map(item => ({
        item,
        lane: item.row !== undefined ? item.row : 0
    }));
  }, [timelineData]);

  useEffect(() => {
    if (!containerRef.current || !zoomRange) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const rCount = Math.max(rowCount, Math.max(...lanes.map(l => l.lane)) + 1);
    const laneHeight = 60;
    const margin = { top: 40, right: 40, bottom: 40, left: 120 };
    const height = margin.top + margin.bottom + (rCount * laneHeight);

    d3.select(container).selectAll('*').remove();

    const svg = d3.select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('display', 'block')
      .on('click', (event) => {
          if (event.target === svg.node()) {
              setSelectedItemId(null);
          }
      });

    const xScale = d3.scaleLinear()
      .domain(zoomRange)
      .range([margin.left, width - margin.right]);

    // Row backgrounds and separators
    for (let i = 0; i < rCount; i++) {
        const y = margin.top + i * laneHeight;
        svg.append('rect')
            .attr('x', 0)
            .attr('y', y)
            .attr('width', width)
            .attr('height', laneHeight)
            .attr('fill', i % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent');
        
        svg.append('line')
            .attr('x1', 0)
            .attr('y1', y + laneHeight)
            .attr('x2', width)
            .attr('y2', y + laneHeight)
            .attr('stroke', '#eee')
            .attr('stroke-width', 1);

        const rowTitle = rowTitles?.[i] || `Civilization ${i + 1}`;

        const labelGroup = svg.append('g')
            .attr('class', 'row-label')
            .attr('transform', `translate(10, ${y + laneHeight / 2})`);

        const labelText = labelGroup.append('text')
            .attr('x', 0)
            .attr('y', 0)
            .attr('dy', '.35em')
            .attr('font-size', '10px')
            .attr('font-weight', 'bold')
            .text(rowTitle);

        if (isEditorMode) {
            labelText.attr('fill', '#3b82f6')
                .style('cursor', 'pointer')
                .on('mouseenter', function() {
                    d3.select(this).attr('fill', '#2563eb').style('text-decoration', 'underline');
                })
                .on('mouseleave', function() {
                    d3.select(this).attr('fill', '#3b82f6').style('text-decoration', 'none');
                })
                .on('click', () => {
                    setEditingRowIdx(i);
                    setEditingRowValue(rowTitle);
                });

            if (rCount > 1) {
                const deleteBtn = labelGroup.append('g')
                    .attr('class', 'delete-row')
                    .attr('transform', 'translate(100, -6)')
                    .style('cursor', 'pointer')
                    .on('click', () => {
                        if (window.confirm(`Delete track "${rowTitle}"? All epochs in this track will move to track 1.`)) {
                            const newItems = timelineData.map(it => {
                                if (it.row === i) return { ...it, row: 0 };
                                if (it.row !== undefined && it.row > i) return { ...it, row: it.row - 1 };
                                return it;
                            });
                            setRowCount(prev => Math.max(1, prev - 1));
                            onUpdate?.(newItems);
                        }
                    });

                deleteBtn.append('circle')
                    .attr('r', 6)
                    .attr('fill', '#fee2e2')
                    .attr('stroke', '#ef4444')
                    .attr('stroke-width', 0.5);

                deleteBtn.append('text')
                    .attr('x', 0)
                    .attr('y', 0)
                    .attr('dy', '.35em')
                    .attr('text-anchor', 'middle')
                    .attr('font-size', '8px')
                    .attr('fill', '#ef4444')
                    .text('×');
            }
        } else {
            labelText.attr('fill', '#999');
        }
    }

    // Grid lines
    svg.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0, ${height - margin.bottom})`)
      .call(d3.axisBottom(xScale)
        .ticks(10)
        .tickFormat(d => {
            const val = d.valueOf();
            if (val < 0) return `${Math.abs(val)} BC`;
            return `${val} AD`;
        })
        .tickSize(-(height - margin.top - margin.bottom))
      )
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick line').attr('stroke', '#ccc').attr('stroke-dasharray', '2,2'))
      .call(g => g.selectAll('.tick text').attr('fill', '#999').attr('font-size', '10px').attr('font-family', 'monospace'));

    const itemsGroup = svg.append('g').attr('class', 'items');

    const dragHandler = d3.drag<any, any>()
        .clickDistance(5)
        .on('start', function(event, d) {
            d3.select(this).raise().attr('opacity', 0.6);
            d.dragged = false;
        })
        .on('drag', function(event, d) {
            if (!isEditorMode) return;
            d.dragged = true;
            const newY = Math.max(margin.top, Math.min(height - margin.bottom - 10, event.y));
            const newLane = Math.floor((newY - margin.top) / laneHeight);
            const currentX = d3.select(this).attr('transform').split(',')[0].replace('translate(', '');
            d3.select(this).attr('transform', `translate(${currentX}, ${newLane * laneHeight})`);
        })
        .on('end', function(event, d) {
            d3.select(this).attr('opacity', 1);
            if (!d.dragged && isEditorMode) {
                 setSelectedItemId(prev => prev === d.item.id ? null : d.item.id);
                 return;
            }
            if (!isEditorMode || !onUpdate) {
                return;
            }
            // Parse transformation to get final lane
            const transform = d3.select(this).attr('transform');
            const match = /translate\(([^,\s]+)[,\s]*([^)\s]*)\)/.exec(transform);
            if (!match) return;
            
            const finalY = parseFloat(match[2] || '0');
            const finalLane = Math.max(0, Math.round(finalY / laneHeight));
            
            const newItems = timelineData.map(it => {
                if (it.id === d.item.id) {
                    return { ...it, row: finalLane };
                }
                return it;
            });
            onUpdate(newItems);
        });

    lanes.forEach(({ item, lane }) => {
      const x = xScale(item.startYear || 0);
      const w = Math.max(2, xScale(item.endYear || 0) - x);
      const rowY = lane * laneHeight; // Offset relative to the group translation
      const itemY = margin.top + (laneHeight * 0.2); // Initial y within group
      const h = laneHeight * 0.6;

      const isSelected = selectedItemId === item.id;
      const g = itemsGroup.append('g')
        .datum({ item, lane })
        .attr('class', `timeline-item ${isSelected ? 'selected' : ''}`)
        .attr('transform', `translate(0, ${rowY})`) // Initial row position
        .style('cursor', 'pointer')
        .on('mouseenter', () => setHoveredItem(item))
        .on('mouseleave', () => setHoveredItem(null))
        .on('click', (event) => {
            event.stopPropagation();
            if (!isEditorMode && item.articleId) {
                onNavigate(item.articleId);
            }
        });
      
      if (isEditorMode) {
        g.call(dragHandler as any);
      }

      const color = item.color || (item.category === 'Ancient Civilisations' ? '#f59e0b' : 
                    item.category === 'History' ? '#10b981' : 
                    item.category === 'Spirituality & Meditation' ? '#8b5cf6' : '#1e293b');

      g.append('rect')
        .attr('x', x)
        .attr('y', itemY)
        .attr('width', w)
        .attr('height', h)
        .attr('rx', 4)
        .attr('fill', color)
        .attr('opacity', 0.9)
        .attr('stroke', isSelected ? '#3b82f6' : color)
        .attr('stroke-width', isSelected ? 3 : 1)
        .attr('filter', isSelected ? 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))' : 'none');

      if (isEditorMode && isSelected) {
        const editBtn = g.append('foreignObject')
            .attr('x', x + w/2 - 10)
            .attr('y', itemY - 22)
            .attr('width', 20)
            .attr('height', 20)
            .style('cursor', 'pointer')
            .on('mousedown pointerdown touchstart', (event) => {
                event.stopPropagation();
            })
            .on('click', (event) => {
                event.stopPropagation();
                const it = timelineData.find(i => i.id === item.id);
                if (it) {
                    setIsAdding(false);
                    setEditingItem(it);
                }
            });

        editBtn.append('xhtml:div')
            .attr('class', 'flex items-center justify-center w-full h-full rounded-full bg-blue-500 border-2 border-white shadow-md text-white')
            .html('<i class="fa-solid fa-pencil" style="font-size: 10px;"></i>');
      }

      // Define a unique clip path for this item to prevent text overflow
      const clipId = `clip-${item.id}`;
      svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('x', x)
        .attr('y', itemY)
        .attr('width', Math.max(0, w))
        .attr('height', h);

      // Calculate sticky visible position
      const visibleX = Math.max(x, xScale(zoomRange[0]));
      const visibleRight = Math.min(x + w, xScale(zoomRange[1]));
      const barVisibleWidth = visibleRight - visibleX;

      g.append('text')
        .attr('x', visibleX + 8)
        .attr('y', itemY + h / 2)
        .attr('dy', '.35em')
        .attr('fill', '#fff')
        .attr('font-size', '11px')
        .attr('font-weight', 'bold')
        .attr('pointer-events', 'none')
        .attr('clip-path', `url(#${clipId})`)
        .text(item.name)
        .each(function() {
            const self = d3.select(this);
            // Hide if the overall bar width is too small OR the visible part is too small
            if (w < 15 || barVisibleWidth < 20) {
                self.attr('opacity', 0);
            } else {
                self.attr('opacity', 1);
            }
        });
    });

  }, [lanes, timelineData, onNavigate, isEditorMode, zoomRange, rowCount, selectedItemId, rowTitles]);

  const handleSaveItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem || !onUpdate) return;

    let newItems: TimelineEvent[];
    if (isAdding) {
        newItems = [...timelineData, { ...editingItem, row: editingItem.row ?? 0 }];
    } else {
        newItems = timelineData.map(d => d.id === editingItem.id ? editingItem : d);
    }
    
    onUpdate(newItems);
    setEditingItem(null);
    setIsAdding(false);
  };

  const handleDeleteItem = () => {
    if (!editingItem || !onUpdate) return;
    const newItems = timelineData.filter(d => d.id !== editingItem.id);
    onUpdate(newItems);
    setEditingItem(null);
    setSelectedItemId(null);
    setIsAdding(false);
  };

  const handleAddNew = () => {
    setIsAdding(true);
    setSelectedItemId(null);
    setEditingItem({
        id: `event-${Date.now()}`,
        name: 'New Epoch',
        startYear: zoomRange ? Math.round(zoomRange[0] + (zoomRange[1] - zoomRange[0]) * 0.4) : 0,
        endYear: zoomRange ? Math.round(zoomRange[0] + (zoomRange[1] - zoomRange[0]) * 0.6) : 100,
        category: 'History',
        row: 0
    });
  };

  const handleAddRow = () => {
    setRowCount(prev => prev + 1);
  };

  return (
    <div className="w-full bg-white dark:bg-stone-800 rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-stone-700 relative">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-serif font-bold text-stone-800 dark:text-stone-100 flex items-center gap-2">
            <i className="fa-solid fa-timeline text-amber-500"></i>
            Chronological Archive
          </h3>
          <p className="text-stone-500 dark:text-stone-400 text-xs mt-1">Navigate historical epochs through the Akashic Wiki.</p>
        </div>
        <div className="flex items-center gap-4">
            {isEditorMode && (
                <div className="flex gap-2 items-center">
                    {selectedItemId && (
                        <button 
                            onClick={() => {
                                const item = timelineData.find(it => it.id === selectedItemId);
                                if (item) {
                                    setIsAdding(false);
                                    setEditingItem(item);
                                }
                            }}
                            className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-amber-600 transition-colors shadow-sm flex items-center gap-1.5"
                        >
                            <i className="fa-solid fa-pen-to-square"></i>
                            Edit Selected
                        </button>
                    )}
                    <button 
                        onClick={handleAddRow}
                        className="px-3 py-1.5 rounded-lg bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-200 text-[10px] font-bold uppercase tracking-wider hover:bg-stone-200 transition-colors shadow-sm"
                    >
                        Add Row
                    </button>
                    <button 
                        onClick={handleAddNew}
                        className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center hover:bg-amber-600 transition-colors shadow-sm"
                        title="Add Event"
                    >
                        <i className="fa-solid fa-plus"></i>
                    </button>
                </div>
            )}
            {hoveredItem && (
                <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800 text-xs font-bold animate-in fade-in zoom-in-95">
                    {hoveredItem.name} (
                    {(hoveredItem.startYear ?? 0) < 0 ? `${Math.abs(hoveredItem.startYear ?? 0)} BC` : hoveredItem.startYear} - 
                    {(hoveredItem.endYear ?? 0) < 0 ? `${Math.abs(hoveredItem.endYear ?? 0)} BC` : hoveredItem.endYear}
                    )
                </div>
            )}
        </div>
      </div>
      
      <div 
        ref={containerRef} 
        className="w-full min-h-[300px] overflow-x-hidden overflow-y-auto custom-scrollbar"
      >
      </div>

      {/* Zoom Sliders */}
      {zoomRange && (
        <div className="mt-8 pt-6 border-t border-stone-100 dark:border-stone-700">
            <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Time Span Navigation</span>
                <span className="text-[10px] font-mono text-stone-500">
                    {zoomRange[0] < 0 ? `${Math.abs(Math.round(zoomRange[0]))} BC` : `${Math.round(zoomRange[0])} AD`} 
                    {' - '}
                    {zoomRange[1] < 0 ? `${Math.abs(Math.round(zoomRange[1]))} BC` : `${Math.round(zoomRange[1])} AD`}
                </span>
            </div>
            <div className="px-2 space-y-4">
                <div className="relative w-full h-8 flex items-center">
                    {/* Track */}
                    <div className="absolute w-full h-1 bg-stone-100 dark:bg-stone-700 rounded-lg"></div>
                    
                    {/* Active Range Highlight */}
                    <div 
                        className="absolute h-1 bg-amber-500 rounded-lg opacity-50"
                        style={{
                            left: `${((zoomRange[0] - fullExtent[0]) / (fullExtent[1] - fullExtent[0])) * 100}%`,
                            width: `${((zoomRange[1] - zoomRange[0]) / (fullExtent[1] - fullExtent[0])) * 100}%`
                        }}
                    ></div>

                    {/* Left Handle (Start Year) */}
                    <input 
                        type="range" 
                        min={fullExtent[0]} 
                        max={fullExtent[1]} 
                        value={zoomRange[0]} 
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setZoomRange([Math.min(val, zoomRange[1] - 500), zoomRange[1]]);
                        }}
                        className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white"
                        style={{ zIndex: zoomRange[0] > (fullExtent[1] - fullExtent[0]) / 2 ? 5 : 4 }}
                    />
                    
                    {/* Right Handle (End Year) */}
                    <input 
                        type="range" 
                        min={fullExtent[0]} 
                        max={fullExtent[1]} 
                        value={zoomRange[1]} 
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            setZoomRange([zoomRange[0], Math.max(val, zoomRange[0] + 500)]);
                        }}
                        className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white"
                        style={{ zIndex: zoomRange[1] < (fullExtent[1] - fullExtent[0]) / 2 ? 5 : 4 }}
                    />
                </div>
            </div>
             <div className="flex justify-between mt-1 px-1">
                <span className="text-[9px] text-stone-400 font-medium">{fullExtent[0] < 0 ? `${Math.abs(Math.round(fullExtent[0]))} BC` : `${Math.round(fullExtent[0])} AD`}</span>
                <button 
                    onClick={() => setZoomRange(fullExtent)} 
                    className="text-[9px] text-amber-500 font-bold uppercase hover:underline"
                >
                    Reset Zoom
                </button>
                <span className="text-[9px] text-stone-400 font-medium">{fullExtent[1] < 0 ? `${Math.abs(Math.round(fullExtent[1]))} BC` : `${Math.round(fullExtent[1])} AD`}</span>
            </div>
        </div>
      )}

      <AnimatePresence>
        {editingItem && (
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-[100] flex items-center justify-center pt-0 px-4 pb-4 bg-black/40 backdrop-blur-sm"
                onClick={() => setEditingItem(null)}
            >
                <div 
                    className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-stone-200 dark:border-stone-700"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between mb-6">
                        <h4 className="text-xl font-serif font-bold text-stone-800 dark:text-stone-100">
                            {isAdding ? 'Add Historical Epoch' : 'Edit Epoch'}
                        </h4>
                        <button onClick={() => setEditingItem(null)} className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSaveItem} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Epoch Name</label>
                            <input 
                                type="text" 
                                value={editingItem.name} 
                                onChange={e => setEditingItem({...editingItem, name: e.target.value})}
                                className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Start Year</label>
                                <input 
                                    type="number" 
                                    value={editingItem.startYear} 
                                    onChange={e => setEditingItem({...editingItem, startYear: parseInt(e.target.value) || 0})}
                                    className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                                    required
                                />
                                <span className="text-[10px] text-stone-400 mt-1 block">Negative for BC</span>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-stone-400 uppercase mb-1">End Year</label>
                                <input 
                                    type="number" 
                                    value={editingItem.endYear} 
                                    onChange={e => setEditingItem({...editingItem, endYear: parseInt(e.target.value) || 0})}
                                    className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Category</label>
                            <select 
                                value={editingItem.category} 
                                onChange={e => setEditingItem({...editingItem, category: e.target.value})}
                                className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                            >
                                {CATEGORIES.map(c => (
                                    <option key={c.label} value={c.label}>{c.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Epoch Color (Optional)</label>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="color" 
                                    value={editingItem.color || '#3b82f6'} 
                                    onChange={e => setEditingItem({...editingItem, color: e.target.value})}
                                    className="w-10 h-10 rounded cursor-pointer bg-transparent border-none p-0"
                                />
                                <button 
                                    type="button" 
                                    onClick={() => setEditingItem({...editingItem, color: undefined})}
                                    className="text-[10px] font-bold text-stone-500 uppercase hover:text-stone-700"
                                >
                                    Use Category Default
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Civilization Track (Row)</label>
                            <select 
                                value={editingItem.row || 0} 
                                onChange={e => setEditingItem({...editingItem, row: parseInt(e.target.value) || 0})}
                                className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                            >
                                {Array.from({ length: rowCount }).map((_, i) => (
                                    <option key={i} value={i}>Track {i + 1}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Link to Article</label>
                            <select 
                                value={editingItem.articleId || ''} 
                                onChange={e => setEditingItem({...editingItem, articleId: e.target.value || undefined})}
                                className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                            >
                                <option value="">No Link</option>
                                {articles.map(article => (
                                    <option key={article.id} value={article.id}>{article.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-3 pt-4">
                            {!isAdding && (
                                <button 
                                    type="button"
                                    onClick={handleDeleteItem}
                                    className="px-4 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg text-sm font-bold transition-colors"
                                >
                                    Delete
                                </button>
                            )}
                            <div className="flex-1"></div>
                            <button 
                                type="button" 
                                onClick={() => setEditingItem(null)}
                                className="px-4 py-2 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 text-sm font-bold"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold transition-colors shadow-sm"
                            >
                                Save Event
                            </button>
                        </div>
                    </form>
                </div>
            </motion.div>
        )}

        {editingRowIdx !== null && (
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="fixed inset-0 z-[100] flex items-center justify-center pt-0 px-4 pb-4 bg-black/40 backdrop-blur-sm"
                onClick={() => setEditingRowIdx(null)}
            >
                <div 
                    className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-stone-200 dark:border-stone-700"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between mb-6">
                        <h4 className="text-lg font-serif font-bold text-stone-800 dark:text-stone-100">
                            Rename Track
                        </h4>
                        <button onClick={() => setEditingRowIdx(null)} className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSaveRowTitle} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Track Name</label>
                            <input 
                                type="text" 
                                value={editingRowValue} 
                                onChange={e => setEditingRowValue(e.target.value)}
                                className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                                required
                                autoFocus
                            />
                        </div>

                        <div className="flex items-center gap-3 pt-4 justify-end">
                            <button 
                                type="button" 
                                onClick={() => setEditingRowIdx(null)}
                                className="px-4 py-2 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 text-sm font-bold"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold transition-colors shadow-sm"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimelineChart;
